import React, { useEffect, useRef, useState } from 'react';
import { API_BASE_URL } from '../config';
import './DeviceIdPicker.css';

/** The two camera SKUs — the only stock that carries a physical device ID. */
const SERIALIZED_SKUS = ['plix-1000', 'plix-1019'];

export const isSerializedSku = (skuCode?: string): boolean =>
  !!skuCode && SERIALIZED_SKUS.includes(skuCode);

interface OrgDevices {
  cosmos_ok: boolean;
  db_ok: boolean;      // false = we could not read what is already out
  assigned: string[];
  out: string[];
  /** device_id -> the date it last went out, for the devices in `out`. */
  out_shipped_on: Record<string, string>;
  available: string[];
  unknown_model: string[];
}

/** Shown when Cosmos is unreachable, the request failed, or the response
 *  shape can't be trusted — a picker that cannot load must not take the
 *  form down with it. */
const OFFLINE_FALLBACK: OrgDevices = {
  cosmos_ok: false, db_ok: false, assigned: [], out: [], out_shipped_on: {},
  available: [], unknown_model: [],
};

/** Normalise a parsed org-devices body so a missing or malformed field
 *  degrades to empty instead of throwing later when we spread it. */
function normalizeOrgDevices(body: any): OrgDevices {
  return {
    cosmos_ok: !!body?.cosmos_ok,
    db_ok: !!body?.db_ok,
    assigned: Array.isArray(body?.assigned) ? body.assigned : [],
    out: Array.isArray(body?.out) ? body.out : [],
    // A plain object or nothing: an array would pass `typeof === 'object'` and
    // then index by number, painting a date onto the wrong chip.
    out_shipped_on:
      body?.out_shipped_on && typeof body.out_shipped_on === 'object'
        && !Array.isArray(body.out_shipped_on)
        ? body.out_shipped_on : {},
    available: Array.isArray(body?.available) ? body.available : [],
    unknown_model: Array.isArray(body?.unknown_model) ? body.unknown_model : [],
  };
}

export interface DeviceIdPickerProps {
  customerTag?: string;
  skuCode?: string;
  quantity: number;
  mode: 'available' | 'out';
  value: string[];
  onChange: (deviceIds: string[]) => void;
}

export const DeviceIdPicker: React.FC<DeviceIdPickerProps> = ({
  customerTag, skuCode, quantity, mode, value, onChange,
}) => {
  const [data, setData] = useState<OrgDevices | null>(null);
  const [manual, setManual] = useState('');

  // A selection is only meaningful for the (customer, SKU) pair it was drawn
  // from: a C8 device on a C6 line, or one customer's device on another's
  // order, is wrong in a way nothing downstream can detect. The picker owns
  // that rule itself rather than leaving each call site to remember it —
  // three call sites forgot, in three different ways.
  const contextKey = `${customerTag ?? ''}|${skuCode ?? ''}`;
  const previousContextKey = useRef<string | null>(null);
  const latest = useRef({ value, onChange });
  useEffect(() => { latest.current = { value, onChange }; });

  useEffect(() => {
    if (previousContextKey.current === contextKey) return;
    const isFirstRun = previousContextKey.current === null;
    previousContextKey.current = contextKey;
    // Never on mount: the parent may legitimately arrive pre-populated (an
    // edit, or a line restored from earlier state).
    if (isFirstRun) return;
    if (latest.current.value.length > 0) latest.current.onChange([]);
  }, [contextKey]);

  useEffect(() => {
    // Always start from a blank pool: on a SKU change (e.g. plix-1000 ->
    // plix-1019) this prevents the previous SKU's devices from staying
    // selectable while the new request is in flight.
    setData(null);
    if (!isSerializedSku(skuCode) || !customerTag) { return; }
    let cancelled = false;
    fetch(`${API_BASE_URL}/api/org-devices/${encodeURIComponent(customerTag)}`
          + `?sku_code=${encodeURIComponent(skuCode!)}`)
      .then(r => (r.ok ? r.json() : Promise.resolve(null)))
      .then(body => { if (!cancelled) setData(body ? normalizeOrgDevices(body) : OFFLINE_FALLBACK); })
      // A picker that cannot load must not take the form down with it.
      .catch(() => { if (!cancelled) setData(OFFLINE_FALLBACK); });
    return () => { cancelled = true; };
  }, [customerTag, skuCode]);

  // Both halves of the context are required, not just the SKU. Without the
  // customer there is no pool to draw from and no org the typed ID could be
  // checked against — and a device tagged onto an INV row (which carries no
  // customer at all) becomes that device's most recent order and quietly
  // drops it out of the customer's return picker.
  if (!isSerializedSku(skuCode) || !customerTag) return null;

  // A return draws on everything we have reason to believe the customer holds,
  // not only the devices Summit itself tagged on an outbound order. A tag is
  // written on a small minority of orders, so `out` alone left ABC Security
  // Service with an empty picker and offered Pelta exactly the one camera we
  // happened to record — while Cosmos listed four for each of them. The ones we
  // did ship come first and keep their date, because that is the stronger claim
  // about where the device is.
  const groups: { key: string; label: string | null; ids: string[] }[] = !data
    ? []
    : mode === 'out'
      ? [
          { key: 'shipped', label: 'On record as shipped', ids: data.out },
          {
            key: 'org',
            label: 'Also assigned to this org in Cosmos',
            ids: [...data.assigned, ...data.unknown_model]
              .filter(d => !data.out.includes(d)),
          },
        ]
      : [{ key: 'available', label: null,
           ids: [...data.available, ...data.unknown_model] }];

  const pool = groups.flatMap(g => g.ids);
  // Anything already selected but absent from the pool — a typed-in ID, or one
  // the pool no longer offers — renders alongside the pool rather than after
  // the container, so it keeps the same chip layout and flex-wrap.
  const strays = value.filter(d => !pool.includes(d));
  const chipGroups = [...groups, { key: 'typed', label: null, ids: strays }]
    .filter(g => g.ids.length > 0);
  // A heading earns its place only when it separates one group from another.
  const showGroupLabels = chipGroups.filter(g => g.label).length > 1;
  const atLimit = value.length >= quantity;

  const toggle = (deviceId: string) => {
    if (value.includes(deviceId)) {
      onChange(value.filter(d => d !== deviceId));
    } else if (!atLimit) {
      onChange([...value, deviceId]);
    }
    // Silently ignored at the limit: the quantity field is the source of truth.
  };

  const addManual = () => {
    const id = manual.trim();
    if (id && !value.includes(id) && !atLimit) { onChange([...value, id]); }
    setManual('');
  };

  // Each mode is measured against the pool it actually offers, so the number in
  // the warning is the number of chips on screen. A return counted `out` alone
  // and so cried shortage while the org's other cameras sat unoffered below it.
  const relevantCount = mode === 'out'
    ? (data ? pool.length : undefined)
    : data?.assigned.length;
  const short = !!data && data.cosmos_ok && relevantCount !== undefined && quantity > relevantCount;

  return (
    <div className="device-picker">
      <label className="device-picker-label">
        Device IDs <span className="device-picker-optional">(optional)</span>
        <span className="device-picker-count">{value.length} of {quantity}</span>
      </label>

      {short && (
        <div className="device-picker-warning" role="alert">
          {mode === 'out'
            ? `We can account for ${pool.length} device${pool.length === 1 ? '' : 's'}`
              + ` with this org — returning ${quantity}.`
            : `Cosmos shows ${data!.assigned.length} assigned for this org — shipping ${quantity}.`}
          {' '}You can continue.
        </div>
      )}

      {data && !data.cosmos_ok && (
        <div className="device-picker-offline">
          Cosmos is unreachable — enter device IDs manually if you know them.
        </div>
      )}

      {data && data.cosmos_ok && !data.db_ok && (
        <div className="device-picker-offline">
          We could not read which devices are currently out — enter device IDs manually if you know them.
        </div>
      )}

      <div className="device-picker-chips">
        {chipGroups.map(group => (
          <React.Fragment key={group.key}>
            {showGroupLabels && group.label && (
              <div className="device-picker-group">{group.label}</div>
            )}
            {group.ids.map(deviceId => (
              <button key={deviceId} type="button"
                      className={`device-chip${value.includes(deviceId) ? ' selected' : ''}`}
                      onClick={() => toggle(deviceId)}>
                {deviceId}
                {data?.out_shipped_on[deviceId] && (
                  <span className="device-chip-date">
                    {data.out_shipped_on[deviceId]}
                  </span>
                )}
              </button>
            ))}
          </React.Fragment>
        ))}
      </div>

      {(!data || !data.cosmos_ok || pool.length === 0) && (
        <div className="device-picker-manual">
          <input type="text" value={manual} placeholder="Enter device ID"
                 onChange={e => setManual(e.target.value)}
                 onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addManual(); } }} />
          <button type="button" onClick={addManual} disabled={atLimit}>Add</button>
        </div>
      )}
    </div>
  );
};
